import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { Session } from '@supabase/supabase-js';
import { formatCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, MIN_PASSWORD, SignIn, Spicy, friendlyAuthError } from '@crave/ui';
import {
  ActionRow,
  DisclosureRow,
  Group,
  InputRow,
  Row,
  SETTINGS_CSS,
  SwitchRow,
} from '../components/Settings.tsx';
import { StripePayment, preloadPaymentForm } from '../components/StripePayment.tsx';
import { api } from '../api.ts';
import {
  authProblem,
  authProviders,
  onSession,
  requestPasswordReset,
  resendConfirmation,
  sendEmailCode,
  sendReauthCode,
  signInWithApple,
  signInWithPassword,
  signOut,
  signOutEverywhere,
  signUpWithPassword,
  updateEmail,
  updatePassword,
  verifyEmailCode,
} from '../lib/auth.ts';
import { cart } from '../lib/cart.ts';
import type { CartLine } from '../lib/types.ts';

interface Me {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  date_of_birth: string | null;
  marketing_opt_in: boolean;
  email_notifications: boolean;
  sms_updates: boolean;
  stats: { orders: number; pizzas: number };
  credit: {
    enabled: boolean;
    balance_cents: number;
    earn_rate_bps: number | null;
    expiry_days: number | null;
  };
  birthday: {
    enabled: boolean;
    has_date: boolean;
    reward: { item_name: string | null; eligible_until: string } | null;
  };
}
interface MyOrder {
  id: string;
  order_number: string;
  status: string;
  pickup_label: string;
  total_cents: number;
  items: { name: string; quantity: number; spicy?: boolean }[];
  track_path: string;
  feedback_given: boolean;
  can_reorder: boolean;
}
interface Ledger {
  balance_cents: number;
  ledger: {
    id: string;
    kind: string;
    amount_cents: number;
    reason: string | null;
    order_number: string | null;
    created_at: string;
  }[];
}
interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

const STATUS: Record<string, string> = {
  COMPLETED: 'Picked up',
  CANCELED: 'Canceled',
  NO_SHOW: 'Not picked up',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partly refunded',
  READY: 'Ready for pickup',
  AWAITING_PAYMENT: 'Payment needed',
  PAYMENT_FAILED: 'Payment needed',
  SCHEDULED: 'Scheduled',
  CONFIRMED: 'Confirmed',
};
const KIND: Record<string, string> = {
  EARN: 'Earned',
  REDEEM: 'Used',
  EXPIRE: 'Expired',
  ADJUST: 'Adjustment',
  REVERSAL: 'Returned',
  BIRTHDAY: 'Birthday',
  SERVICE_RECOVERY: 'From the team',
};

export function AccountPage() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [apple, setApple] = useState(false);
  const [params] = useSearchParams();
  useEffect(() => onSession(setSession), []);
  useEffect(() => {
    void authProviders().then((p) => setApple(p.apple));
  }, []);
  if (session === undefined && !authProblem) return <p>Loading…</p>;
  if (!session)
    return (
      <SignIn
        key={params.get('mode') ?? 'signin'}
        title={params.get('mode') === 'signup' ? 'Create your account' : 'Sign in'}
        subtitle="Your orders, Crave Credit and birthday pizza in one place."
        problem={authProblem}
        appleEnabled={apple}
        initialMode={params.get('mode') === 'signup' ? 'signup' : 'signin'}
        askName
        onApple={() => signInWithApple('/account')}
        onPassword={signInWithPassword}
        onSendCode={(email) => sendEmailCode(email, '/account')}
        onVerifyCode={verifyEmailCode}
        onForgotPassword={requestPasswordReset}
        onResendConfirmation={(email) => resendConfirmation(email, '/account')}
        onSignUp={(input) => signUpWithPassword(input, '/account')}
        footer={
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>
            Ordered before as a guest? Use the same email and those orders show up here once your
            email is confirmed.
          </p>
        }
      />
    );
  return <Account />;
}

function Account() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [msg, setMsg] = useState<string | null>(null);
  const fail = (e: unknown) =>
    setMsg(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.');
  const me = useQuery({ queryKey: ['me'], queryFn: async () => (await api.get<Me>('/me')).data });
  const orders = useQuery({
    queryKey: ['me-orders'],
    queryFn: async () => (await api.get<{ items: MyOrder[] }>('/me/orders')).data.items,
  });
  const credit = useQuery({
    queryKey: ['me-credit'],
    queryFn: async () => (await api.get<Ledger>('/me/credit')).data,
  });
  const cards = useQuery({
    queryKey: ['me-cards'],
    queryFn: async () =>
      (await api.get<{ available: boolean; cards: SavedCard[] }>('/me/payment-methods')).data,
  });
  const reorder = useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<{ lines: Omit<CartLine, 'key'>[]; skipped: string[] }>(
          `/me/orders/${id}/reorder`,
        )
      ).data,
    onSuccess: (r) => {
      for (const l of r.lines) cart.add(l);
      void nav('/cart');
    },
    onError: fail,
  });
  const removeCard = useMutation({
    mutationFn: (id: string) => api.delete(`/me/payment-methods/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me-cards'] }),
    onError: fail,
  });
  const deleteAccount = useMutation({
    mutationFn: () => api.delete('/me'),
    onSuccess: async () => {
      await signOut();
      cart.clear();
      void nav('/');
    },
    onError: fail,
  });
  const [allOrders, setAllOrders] = useState(false);

  if (me.isError)
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <Card>
          <p style={{ margin: 0 }}>
            {me.error instanceof ApiError ? me.error.message : "We couldn't load your account."}
          </p>
        </Card>
        <Button variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    );
  if (!me.data) return <p>Loading your account…</p>;
  const m = me.data;
  const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ');
  const initials =
    [m.first_name, m.last_name]
      .map((n) => n?.trim()[0])
      .filter(Boolean)
      .join('')
      .toUpperCase() || m.email[0]?.toUpperCase();
  const list = orders.data ?? [];
  const shown = allOrders ? list : list.slice(0, 4);
  const refreshMe = () => void qc.invalidateQueries({ queryKey: ['me'] });

  return (
    <div className="st-page" style={{ paddingTop: 8 }}>
      <style>{SETTINGS_CSS + ACCOUNT_CSS}</style>

      <header className="ac-hero">
        <div className="ac-avatar" aria-hidden="true">
          {initials}
        </div>
        <h1 className="ac-name">{fullName || 'Your account'}</h1>
        <p className="ac-email">{m.email}</p>
        <p className="ac-stats">
          <span>
            <strong>{m.stats.orders}</strong> {m.stats.orders === 1 ? 'order' : 'orders'}
          </span>
          <span>
            <strong>{m.stats.pizzas}</strong> {m.stats.pizzas === 1 ? 'pizza' : 'pizzas'}
          </span>
        </p>
      </header>

      {msg && (
        <p className="st-note" data-error="" role="alert" style={{ textAlign: 'center' }}>
          {msg}
        </p>
      )}

      {m.birthday.reward && <BirthdayReward reward={m.birthday.reward} />}

      {(m.credit.enabled || m.credit.balance_cents > 0) && (
        <section className="ac-wallet" aria-label="Crave Credit">
          <div className="ac-wallet-top">
            <span className="ac-wallet-label">Crave Credit</span>
            <span className="ac-wallet-mark" aria-hidden="true">
              CRAVE
            </span>
          </div>
          <div className="ac-wallet-balance">{formatCents(m.credit.balance_cents)}</div>
          <p className="ac-wallet-note">
            {m.credit.earn_rate_bps
              ? `${m.credit.earn_rate_bps / 100}% back on food, every order. `
              : ''}
            Use it at checkout.
            {m.credit.expiry_days ? ` Lasts ${m.credit.expiry_days} days.` : ''}
          </p>
          {credit.data && credit.data.ledger.length > 0 && (
            <details className="ac-wallet-history">
              <summary>Activity</summary>
              <ul>
                {credit.data.ledger.map((t) => (
                  <li key={t.id}>
                    <span>
                      {KIND[t.kind] ?? t.kind}
                      {t.order_number ? ` · ${t.order_number}` : ''}
                      <small>{new Date(t.created_at).toLocaleDateString()}</small>
                    </span>
                    <strong>{formatCents(t.amount_cents)}</strong>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      <Group
        title="Orders"
        note={
          list.length > 4 ? (
            <button type="button" className="st-capsule" onClick={() => setAllOrders((v) => !v)}>
              {allOrders ? 'Show recent orders' : `Show all ${list.length} orders`}
            </button>
          ) : undefined
        }
      >
        {orders.isLoading && <Row label="Loading your orders…" />}
        {orders.data?.length === 0 && (
          <Row label="No orders yet" detail="Your pizzas show up here." to="/" value="Menu" />
        )}
        {shown.map((o) => (
          <div key={o.id} className="ac-order">
            <Row
              to={o.track_path}
              label={o.pickup_label}
              detail={
                <>
                  {o.order_number} ·{' '}
                  {o.items.map((i, n) => (
                    <span key={n}>
                      {n > 0 ? ', ' : ''}
                      {i.quantity} × {i.name}
                      {i.spicy && (
                        <>
                          {' '}
                          <Spicy size="sm" />
                        </>
                      )}
                    </span>
                  ))}
                </>
              }
              trailing={
                <span className="ac-order-end">
                  <strong>{formatCents(o.total_cents)}</strong>
                  <span className="st-pill" data-tone={statusTone(o.status)}>
                    {STATUS[o.status] ?? 'In progress'}
                  </span>
                </span>
              }
            />
            {(o.status === 'AWAITING_PAYMENT' || o.status === 'PAYMENT_FAILED') && (
              <div className="ac-order-actions">
                <Link to={o.track_path} className="ac-pay-now">
                  {o.status === 'PAYMENT_FAILED' ? 'Try Again' : 'Finish Payment'}
                </Link>
              </div>
            )}
            {((o.status === 'COMPLETED' && !o.feedback_given) || o.can_reorder) && (
              <div className="ac-order-actions">
                {o.can_reorder && (
                  <button
                    type="button"
                    className="st-capsule"
                    onClick={() => reorder.mutate(o.id)}
                    disabled={reorder.isPending}
                  >
                    Order again
                  </button>
                )}
                {o.status === 'COMPLETED' && !o.feedback_given && (
                  <Link to={`${o.track_path}#feedback`} className="st-capsule">
                    Rate it
                  </Link>
                )}
              </div>
            )}
          </div>
        ))}
      </Group>

      <Notifications me={m} onSaved={refreshMe} />

      <Details me={m} onSaved={refreshMe} />

      {m.birthday.enabled && !m.birthday.has_date && !m.birthday.reward && (
        <BirthdaySetup onSaved={refreshMe} />
      )}

      {cards.data?.available && (
        <Payment
          cards={cards.data.cards}
          email={m.email}
          onRemove={(id) => removeCard.mutate(id)}
          removing={removeCard.isPending}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['me-cards'] })}
        />
      )}

      <LoginSecurity email={m.email} />

      <Group>
        <ActionRow onClick={() => void signOut()}>Sign out</ActionRow>
      </Group>
      <Group note="Your Crave Credit and birthday pizza go with your account. Past receipts stay with us for our books, without your name.">
        <ActionRow
          tone="danger"
          disabled={deleteAccount.isPending}
          onClick={() => {
            if (
              confirm(
                'Delete your account? Your Crave Credit and birthday pizza go with it. Past receipts stay with us for our books, without your name.',
              )
            )
              deleteAccount.mutate();
          }}
        >
          Delete account
        </ActionRow>
      </Group>
    </div>
  );
}

const ACCOUNT_CSS = `
.ac-hero { display: grid; justify-items: center; gap: 2px; text-align: center; padding: 8px 0 0; }
.ac-avatar { width: 84px; height: 84px; border-radius: 50%; display: grid; place-items: center;
  background: linear-gradient(145deg, #e0342b, var(--crave-red) 55%, var(--crave-red-dark));
  color: #fff; font-size: 32px; font-weight: 700; letter-spacing: .02em; margin-bottom: 10px;
  box-shadow: 0 8px 24px rgba(200,27,34,.25); }
.ac-name { margin: 0; font-size: 28px; line-height: 1.15; font-weight: 700; letter-spacing: -.01em; }
.ac-email { margin: 0; color: var(--ink-soft); overflow-wrap: anywhere; }
.ac-stats { margin: 10px 0 0; display: flex; gap: 8px; }
.ac-stats span { padding: 4px 12px; border-radius: 999px; background: var(--paper); font-size: 14px;
  box-shadow: 0 0 0 1px rgba(20,20,20,.06); }

.ac-wallet { position: relative; overflow: hidden; border-radius: 20px; padding: 20px 20px 16px; color: #fff;
  background: radial-gradient(120% 140% at 100% 0%, #3a3a3a 0%, var(--ink) 55%); display: grid; gap: 4px;
  box-shadow: 0 14px 34px rgba(20,20,20,.22); }
.ac-wallet::after { content: ''; position: absolute; right: -56px; top: -76px; width: 170px; height: 170px;
  border-radius: 50%; background: var(--crave-red); }
.ac-wallet > * { position: relative; z-index: 1; }
.ac-wallet-top { display: flex; justify-content: space-between; align-items: center; }
.ac-wallet-label { font-size: 14px; font-weight: 600; opacity: .8; }
.ac-wallet-mark { font-size: 13px; font-weight: 800; letter-spacing: .18em; }
.ac-wallet-balance { font-size: 40px; font-weight: 700; letter-spacing: -.02em; line-height: 1.1; margin-top: 18px; }
.ac-wallet-note { margin: 0; font-size: 14px; opacity: .85; max-width: 32ch; }
.ac-wallet-history summary { cursor: pointer; min-height: 44px; display: inline-flex; align-items: center;
  font-size: 14px; font-weight: 600; list-style: none; }
.ac-wallet-history summary::-webkit-details-marker { display: none; }
.ac-wallet-history summary::after { content: '›'; margin-left: 6px; font-size: 18px; transition: transform .2s; }
.ac-wallet-history[open] summary::after { transform: rotate(90deg); }
.ac-wallet-history ul { list-style: none; margin: 0; padding: 8px 12px; border-radius: 12px;
  background: rgba(255,255,255,.1); display: grid; gap: 8px; font-size: 14px; }
.ac-wallet-history li { display: flex; justify-content: space-between; gap: 12px; }
.ac-wallet-history small { display: block; opacity: .7; }

.ac-order-end { display: grid; justify-items: end; gap: 4px; }
.ac-order .st-row { align-items: flex-start; }
.ac-order .st-chevron { align-self: center; }
.ac-order-actions { display: flex; gap: 20px; padding: 0 16px 6px; margin-top: -8px; }
.ac-pay-now { display: inline-flex; align-items: center; min-height: 40px; padding: 0 18px; margin: 2px 0 8px;
  border-radius: 999px; background: var(--crave-red); color: #fff; font-weight: 600; text-decoration: none; }

.ac-birthday { border-radius: 20px; padding: 18px 20px; color: #fff; display: grid; gap: 6px;
  background: linear-gradient(135deg, #e0342b, var(--crave-red) 60%, var(--crave-red-dark)); }
.ac-birthday strong { font-size: 20px; }
.ac-birthday p { margin: 0; opacity: .92; }
.ac-birthday a { color: #fff; font-weight: 700; min-height: 44px; display: inline-flex; align-items: center; }
`;

const LIVE = new Set([
  'READY',
  'CONFIRMED',
  'SCHEDULED',
  'QUEUED',
  'PREPARING',
  'IN_OVEN',
  'BOXING',
]);
function statusTone(status: string): 'live' | 'done' | undefined {
  if (LIVE.has(status) || status === 'AWAITING_PAYMENT' || status === 'PAYMENT_FAILED')
    return 'live';
  if (status === 'COMPLETED') return 'done';
  return undefined;
}

function BirthdayReward({ reward }: { reward: NonNullable<Me['birthday']['reward']> }) {
  const until = new Date(`${reward.eligible_until}T12:00:00`).toLocaleDateString([], {
    month: 'long',
    day: 'numeric',
  });
  return (
    <section className="ac-birthday">
      <strong>Happy birthday. Your pizza is on us.</strong>
      <p>
        {reward.item_name ? `A free ${reward.item_name}` : 'A free pizza'} comes off automatically
        at checkout until {until}.
      </p>
      <Link to="/">Order now ›</Link>
    </section>
  );
}

/** A free pizza in the birthday month. The date is set once, so it opens on purpose. */
/**
 * Cards on file (#67). A card can be added here, not only on the way through checkout, so somebody
 * who wants a Saturday order to go through in two taps can set it up on a Tuesday. Stripe's script
 * is fetched when the row is opened, never on the way into the account.
 */
function Payment({
  cards,
  email,
  onRemove,
  removing,
  onSaved,
}: {
  cards: SavedCard[];
  email: string;
  onRemove: (id: string) => void;
  removing: boolean;
  onSaved: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState(false);
  const setup = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ client_secret: string; publishable_key: string; provider: string }>(
          '/me/payment-methods/setup',
        )
      ).data,
  });
  const start = () => {
    setSaved(false);
    setAdding(true);
    // Fetch Stripe and ask for the SetupIntent at the same time, so neither waits on the other.
    preloadPaymentForm();
    setup.mutate();
  };
  const close = () => {
    setAdding(false);
    setup.reset();
  };
  return (
    <Group
      title="Payment"
      noteError={setup.isError}
      note={
        setup.isError
          ? setup.error instanceof ApiError
            ? setup.error.message
            : 'We could not start that. Please try again.'
          : saved
            ? 'Card saved. Checkout will offer it next time.'
            : cards.length > 0
              ? 'Used at checkout, and for orders placed further ahead than a card can be held.'
              : 'Add a card now and checkout is two taps on a Saturday.'
      }
    >
      {cards.map((k) => (
        <Row
          key={k.id}
          label={
            <span style={{ textTransform: 'capitalize' }}>
              {k.brand} •••• {k.last4}
            </span>
          }
          detail={`Expires ${String(k.exp_month).padStart(2, '0')}/${String(k.exp_year).slice(-2)}`}
          trailing={
            <button
              type="button"
              className="st-capsule"
              onClick={() => onRemove(k.id)}
              disabled={removing}
            >
              Remove
            </button>
          }
        />
      ))}
      {!adding && <ActionRow onClick={start}>Add a card</ActionRow>}
      {adding && (
        <div className="st-block" style={{ display: 'grid', gap: 14 }}>
          {setup.isPending && <p style={{ margin: 0 }}>Getting a secure form…</p>}
          {setup.data && (
            <StripePayment
              purpose="save_card"
              publishableKey={setup.data.publishable_key}
              clientSecret={setup.data.client_secret}
              email={email}
              returnUrl={`${window.location.origin}/account`}
              onSaved={() => {
                setSaved(true);
                close();
                onSaved();
              }}
            />
          )}
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
        </div>
      )}
    </Group>
  );
}

function BirthdaySetup({ onSaved }: { onSaved: () => void }) {
  const [dob, setDob] = useState('');
  const save = useMutation({
    mutationFn: () => api.patch('/me', { date_of_birth: dob }),
    onSuccess: onSaved,
  });
  return (
    <Group
      title="Birthday pizza"
      noteError={save.isError}
      note={
        save.isError
          ? save.error instanceof ApiError
            ? save.error.message
            : 'That did not save. Please try again.'
          : 'A free pizza in your birthday month. You can set your birthday once.'
      }
    >
      <DisclosureRow label="Add your birthday" detail="Get a pizza on us every year">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          style={{ display: 'grid', gap: 12 }}
        >
          <Field
            label="Birthday"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            required
          />
          <Button type="submit" disabled={save.isPending || !dob}>
            {save.isPending ? 'Saving…' : 'Save birthday'}
          </Button>
        </form>
      </DisclosureRow>
    </Group>
  );
}

/** "(863) 777-3933" from whatever was saved, or null when it is not a US number. */
function usPhone(typed: string | null): string | null {
  if (!typed) return null;
  let d = typed.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(d)
    ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    : null;
}

/** Switches that save the moment they are flipped, the way Settings does. */
function Notifications({ me, onSaved }: { me: Me; onSaved: () => void }) {
  const [email, setEmail] = useState(me.email_notifications);
  const [texts, setTexts] = useState(me.sms_updates);
  const [askPhone, setAskPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const saved = usPhone(me.phone);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch('/me', body),
    onSuccess: () => {
      setError(null);
      setAskPhone(false);
      onSaved();
    },
    onError: (e, body) => {
      if ('email_notifications' in body) setEmail(me.email_notifications);
      if ('sms_updates' in body) setTexts(me.sms_updates);
      setError(e instanceof ApiError ? e.message : 'That did not save. Please try again.');
    },
  });

  function flipTexts(next: boolean) {
    setError(null);
    if (next && !saved) {
      setAskPhone(true);
      return;
    }
    setTexts(next);
    save.mutate({ sms_updates: next });
  }

  function turnOnWithPhone(e: FormEvent) {
    e.preventDefault();
    if (!usPhone(phone)) {
      setError('Enter a US mobile number, like (863) 555-0100.');
      return;
    }
    setTexts(true);
    save.mutate({ phone, sms_updates: true });
  }

  return (
    <Group
      title="Notifications"
      noteError={Boolean(error)}
      note={
        error ?? (
          <>
            Texts are up to 3 per order: booked, ready, and if it is canceled. Message and data
            rates may apply. Reply STOP to any text to opt out.{' '}
            <Link to="/texts" style={{ padding: '14px 4px' }}>
              Text terms
            </Link>
          </>
        )
      }
    >
      <SwitchRow
        label="Email updates"
        detail="Confirmations, ready notices and receipts"
        checked={email}
        disabled={save.isPending}
        onChange={(next) => {
          setEmail(next);
          save.mutate({ email_notifications: next });
        }}
      />
      <SwitchRow
        label="Text updates"
        detail={
          texts && saved
            ? `To ${saved}`
            : saved
              ? `Texts about your orders to ${saved}`
              : 'Needs a US mobile number'
        }
        checked={texts || askPhone}
        disabled={save.isPending}
        onChange={(next) => (askPhone && !next ? setAskPhone(false) : flipTexts(next))}
      />
      {askPhone && (
        <form onSubmit={turnOnWithPhone} className="st-block" style={{ gap: 10 }}>
          <Field
            label="Mobile number for texts"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(863) 555-0100"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoFocus
            required
          />
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Turning on…' : 'Turn on text updates'}
          </Button>
        </form>
      )}
    </Group>
  );
}

/** Name, mobile and birthday, edited in place. Save appears once something changed. */
function Details({ me, onSaved }: { me: Me; onSaved: () => void }) {
  const [first, setFirst] = useState(me.first_name ?? '');
  const [last, setLast] = useState(me.last_name ?? '');
  const [phone, setPhone] = useState(usPhone(me.phone) ?? me.phone ?? '');
  const [note, setNote] = useState<{ error: boolean; text: string } | null>(null);
  const startPhone = usPhone(me.phone) ?? me.phone ?? '';
  const dirty =
    first !== (me.first_name ?? '') || last !== (me.last_name ?? '') || phone !== startPhone;

  const save = useMutation({
    mutationFn: () =>
      api.patch('/me', {
        first_name: first,
        last_name: last,
        ...(phone !== startPhone ? { phone } : {}),
      }),
    onSuccess: () => {
      setNote({ error: false, text: 'Saved.' });
      onSaved();
    },
    onError: (e) =>
      setNote({
        error: true,
        text: e instanceof ApiError ? e.message : 'That did not save. Please try again.',
      }),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setNote(null);
        save.mutate();
      }}
      style={{ display: 'grid', gap: 12 }}
    >
      <Group
        title="Your details"
        note={
          note?.text ??
          (me.birthday.has_date ? 'A free pizza shows up here in your birthday month.' : undefined)
        }
        noteError={note?.error}
      >
        <InputRow
          id="ac-first"
          label="First name"
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          autoComplete="given-name"
          placeholder="Add"
        />
        <InputRow
          id="ac-last"
          label="Last name"
          value={last}
          onChange={(e) => setLast(e.target.value)}
          autoComplete="family-name"
          placeholder="Add"
        />
        <InputRow
          id="ac-phone"
          label="Mobile"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          placeholder="Add"
        />
        {me.birthday.enabled && me.birthday.has_date && <Row label="Birthday" value="Saved" />}
      </Group>
      {dirty && (
        <Button type="submit" disabled={save.isPending} style={{ width: '100%' }}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      )}
    </form>
  );
}

/** Password, email and sessions, all self-service. */
function LoginSecurity({ email }: { email: string }) {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [nonce, setNonce] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState<'password' | 'email' | 'everywhere' | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setMsg({ kind: 'error', text: `Use at least ${MIN_PASSWORD} characters.` });
      return;
    }
    setBusy('password');
    setMsg(null);
    const r = await updatePassword(password, needsCode ? nonce.trim() : undefined);
    if (r.needsCode && !needsCode) {
      const err = await sendReauthCode();
      setNeedsCode(true);
      setMsg(
        err
          ? { kind: 'error', text: friendlyAuthError(err) }
          : { kind: 'ok', text: `For security we emailed a code to ${email}. Enter it below.` },
      );
    } else if (r.error) setMsg({ kind: 'error', text: friendlyAuthError(r.error) });
    else if (r.needsCode)
      setMsg({ kind: 'error', text: 'That code did not work. Check the newest email.' });
    else {
      setPassword('');
      setNonce('');
      setNeedsCode(false);
      setMsg({ kind: 'ok', text: 'Password saved. Use it next time you sign in.' });
    }
    setBusy(null);
  }

  async function saveEmail(e: FormEvent) {
    e.preventDefault();
    setBusy('email');
    setMsg(null);
    const err = await updateEmail(newEmail, '/account');
    setMsg(
      err
        ? { kind: 'error', text: friendlyAuthError(err) }
        : {
            kind: 'ok',
            text: `Check ${newEmail.trim()} for a confirmation link. Your email changes once you confirm.`,
          },
    );
    if (!err) setNewEmail('');
    setBusy(null);
  }

  return (
    <Group title="Sign-in and security" note={msg?.text} noteError={msg?.kind === 'error'}>
      <DisclosureRow label="Password" detail="Set one, or change the one you have">
        <form onSubmit={savePassword} style={{ display: 'grid', gap: 12 }}>
          <Field
            label="New password"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={MIN_PASSWORD}
            hint={`At least ${MIN_PASSWORD} characters.`}
          />
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 14,
              minHeight: 'var(--touch)',
            }}
          >
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            Show password
          </label>
          {needsCode && (
            <Field
              label="Code from the email"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={nonce}
              onChange={(e) => setNonce(e.target.value.replace(/\s/g, ''))}
              required
            />
          )}
          <Button type="submit" disabled={busy !== null}>
            {busy === 'password' ? 'Saving…' : 'Save password'}
          </Button>
        </form>
      </DisclosureRow>
      <DisclosureRow label="Email" detail={email}>
        <form onSubmit={saveEmail} style={{ display: 'grid', gap: 12 }}>
          <Field
            label="New email"
            type="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            hint="Receipts and sign-in emails go here once you confirm it."
          />
          <Button type="submit" variant="secondary" disabled={busy !== null}>
            {busy === 'email' ? 'Sending…' : 'Change email'}
          </Button>
        </form>
      </DisclosureRow>
      <ActionRow
        disabled={busy !== null}
        onClick={() => {
          setBusy('everywhere');
          void signOutEverywhere().finally(() => setBusy(null));
        }}
      >
        {busy === 'everywhere' ? 'Signing out…' : 'Sign out of all devices'}
      </ActionRow>
    </Group>
  );
}
